// pages/ConsultationRoom.tsx
// ユーザー向け問診ルームページ（Zoom 埋め込み ＋ 医師メッセージパネル）
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, AlertTriangle, ArrowLeft, Calendar, Clock } from 'lucide-react';
import api from '../api/axios';
import ZoomEmbed from '../components/ZoomEmbed';
import usePresenceHeartbeat from '../hooks/usePresenceHeartbeat';

interface Reservation {
  id: number;
  date: string;
  time: string;
  service: string;
  status: string;
  zoom_join_url: string | null;
  zoom_meeting_id: string | null;
  doctor_name: string | null;
  needs_translation?: boolean;
  user_profile?: { full_name?: string | null };
}

interface DoctorMessage {
  id: number;
  content: string;
  created_at: string;
  read_at: string | null;
}

// 相対時間表示
function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const min  = Math.floor(diff / 60000);
  if (min < 1)  return 'たった今';
  if (min < 60) return `${min}分前`;
  const h = Math.floor(min / 60);
  if (h < 24)   return `${h}時間前`;
  const d = Math.floor(h / 24);
  if (d < 30)   return `${d}日前`;
  return new Date(dateStr).toLocaleDateString('ja-JP');
}

// 折りたたみメッセージカード
const MessageCard: React.FC<{ msg: DoctorMessage }> = ({ msg }) => {
  const [open, setOpen] = useState(false);
  const preview = msg.content.length > 60 ? msg.content.slice(0, 60) + '…' : msg.content;

  return (
    <div
      onClick={() => setOpen(v => !v)}
      className="w-full text-left text-xs bg-white border border-gray-100 rounded-xl p-3.5 hover:border-blue-200 hover:bg-blue-50/40 transition-all cursor-pointer shadow-sm"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-gray-700 whitespace-pre-wrap leading-relaxed flex-1">
          {open ? msg.content : preview}
        </p>
        <span className="shrink-0 mt-0.5 text-gray-300">
          {open
            ? <ChevronUp size={13} />
            : <ChevronDown size={13} />
          }
        </span>
      </div>
      <p className="text-gray-400 text-[10px] mt-2">{timeAgo(msg.created_at)}</p>
    </div>
  );
};

// 退出確認ダイアログ
const LeaveDialog: React.FC<{ onConfirm: () => void; onCancel: () => void }> = ({ onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 flex flex-col gap-4">
      <div className="flex items-center gap-3 text-amber-500">
        <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
          <AlertTriangle size={20} />
        </div>
        <span className="font-bold text-gray-800 text-base">ミーティングを退出しますか？</span>
      </div>
      <p className="text-sm text-gray-500 leading-relaxed pl-1">
        ページを離れるとビデオ通話が切断されます。<br />
        退出してもよろしいですか？
      </p>
      <div className="flex gap-2.5 justify-end pt-1">
        <button
          onClick={onCancel}
          className="px-5 py-2 text-sm font-medium rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
        >
          キャンセル
        </button>
        <button
          onClick={onConfirm}
          className="px-5 py-2 text-sm font-semibold rounded-xl bg-red-500 text-white hover:bg-red-600 transition shadow-sm"
        >
          退出する
        </button>
      </div>
    </div>
  </div>
);

const ConsultationRoom: React.FC = () => {
  const { reservationId } = useParams<{ reservationId: string }>();
  const navigate = useNavigate();
  // 患者が診療室にいる間、在席ハートビートを送る（医師側の在席ランプ点灯用）
  usePresenceHeartbeat({ source: 'consultation' });
  const { t } = useTranslation();
  const rid = Number(reservationId);

  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [messages, setMessages] = useState<DoctorMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [doctorPresence, setDoctorPresence] = useState<string>('offline');  // 医師の在席状態（双方向可視化）
  const pendingNavRef = useRef<(() => void) | null>(null);

  // 医師の在席状態をポーリング（患者側にも「医師が入室中」を可視化）
  useEffect(() => {
    if (!rid) return;
    let stop = false;
    const pollDoctorPresence = async () => {
      try {
        const res = await api.get<{ presence: string }>(`/reservations/${rid}/doctor-presence`);
        if (!stop) setDoctorPresence(res.data?.presence || 'offline');
      } catch { /* 表示に影響しないため無視 */ }
    };
    pollDoctorPresence();
    const pid = window.setInterval(() => { if (!stop) pollDoctorPresence(); }, 20000);
    return () => { stop = true; window.clearInterval(pid); };
  }, [rid]);

  // ブラウザのタブ閉じ・リロードをガード
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // マイページへ戻るボタン
  const handleBackToMypage = useCallback(() => {
    pendingNavRef.current = () => navigate('/mypage');
    setShowLeaveDialog(true);
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resRes, msgRes] = await Promise.allSettled([
          api.get<Reservation>(`/reservations/${rid}`),
          api.get<DoctorMessage[]>(`/doctor-messages/${rid}`),
        ]);
        if (resRes.status === 'fulfilled') setReservation(resRes.value.data);
        if (msgRes.status === 'fulfilled') setMessages(msgRes.value.data);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [rid]);

  // 未読メッセージを既読にする
  useEffect(() => {
    if (messages.length === 0) return;
    messages
      .filter((m) => !m.read_at)
      .forEach((m) => { api.patch(`/doctor-messages/${m.id}/read`).catch(() => {}); });
  }, [messages]);

  const handleLeaveConfirm = () => {
    setShowLeaveDialog(false);
    const nav = pendingNavRef.current;
    pendingNavRef.current = null;
    nav?.();
  };

  const handleLeaveCancel = () => {
    setShowLeaveDialog(false);
    pendingNavRef.current = null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 text-gray-400 text-sm">
        {t('loading') || '読み込み中...'}
      </div>
    );
  }

  if (!reservation) {
    return <div className="p-6 text-red-500">{t('mypage.reservation_not_found') || '予約が見つかりません。'}</div>;
  }

  const patientFullName = reservation.user_profile?.full_name;
  const zoomDisplayName = patientFullName
    ? `${patientFullName}（患者）`
    : (t('consultation.patient_display_name') || '患者');

  const messagesHeader = reservation.doctor_name
    ? `${reservation.doctor_name}医師からのメッセージ`
    : (t('consultation.doctor_messages') || '医師からのメッセージ');

  const serviceLabel = t(`service.${reservation.service}`, reservation.service);

  return (
    <div className="flex flex-col h-screen bg-gray-950">

      {/* ── ヘッダー ── */}
      <header className="shrink-0 flex items-center gap-3 px-4 py-0 bg-gray-900 border-b border-gray-800 h-12">
        {/* ロゴ */}
        <img src="/images/Melodislogo.png" className="h-6 w-auto brightness-0 invert opacity-90" alt="Melodis" />

        <div className="w-px h-5 bg-gray-700" />

        {/* 診療情報 */}
        <div className="flex items-center gap-2.5 text-xs text-gray-400 flex-1 min-w-0">
          <span className="font-semibold text-gray-200 truncate">{serviceLabel}</span>
          <span className="flex items-center gap-1 shrink-0">
            <Calendar size={11} className="text-gray-500" />
            {reservation.date}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            <Clock size={11} className="text-gray-500" />
            {reservation.time}
          </span>
        </div>

        {/* マイページへ戻るボタン */}
        <button
          onClick={handleBackToMypage}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-300 border border-gray-700 hover:border-gray-500 hover:text-white hover:bg-gray-800 transition"
        >
          <ArrowLeft size={12} />
          マイページ
        </button>
      </header>

      {/* ── メインコンテンツ ── */}
      <div className="flex flex-1 overflow-hidden md:flex-row flex-col">

        {/* Zoom エリア */}
        <div className="md:flex-1 w-full bg-gray-950 flex flex-col overflow-hidden">
          {/* 医師在席ランプ（入室済み=緑 / オンライン未入室=橙 / オフライン=灰） */}
          <div className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 border-b border-gray-800 text-[11px] text-gray-300">
            <span className={`inline-block w-2 h-2 rounded-full ${doctorPresence === 'offline' ? 'bg-gray-500' : doctorPresence === 'in_room' ? 'bg-green-500' : 'bg-amber-400'}`} />
            {doctorPresence === 'in_room' ? '医師が診療室に入室しています' : doctorPresence === 'online' ? '医師オンライン（未入室）' : '医師はまだ入室していません'}
          </div>
          {reservation.zoom_join_url && reservation.zoom_meeting_id ? (
            <ZoomEmbed
              meetingNumber={reservation.zoom_meeting_id}
              displayName={zoomDisplayName}
              joinUrl={reservation.zoom_join_url}
              needsTranslation={reservation.needs_translation}
            />
          ) : (
            <div className="flex items-center justify-center flex-1 text-gray-500 text-sm">
              {t('consultation.no_zoom_link') || 'Zoom リンクが設定されていません'}
            </div>
          )}
        </div>

        {/* 医師メッセージパネル */}
        {messages.length > 0 && (
          <div className="md:w-72 w-full bg-gray-900 border-l border-gray-800 flex flex-col overflow-hidden">
            {/* パネルヘッダー */}
            <div className="shrink-0 px-4 py-3 border-b border-gray-800">
              <p className="text-xs font-semibold text-gray-200">{messagesHeader}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{messages.length}件のメッセージ</p>
            </div>
            {/* メッセージリスト */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {messages.map((msg) => (
                <MessageCard key={msg.id} msg={msg} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 退出確認ダイアログ */}
      {showLeaveDialog && (
        <LeaveDialog onConfirm={handleLeaveConfirm} onCancel={handleLeaveCancel} />
      )}
    </div>
  );
};

export default ConsultationRoom;
